import React from 'react';



type MenuExpandedContextValue = {
	isExpanded: boolean;
	isMinimized: boolean;
	setExpanded: (expanded: boolean) => void;
	setMinimized: (minimized: boolean) => void;
};

const MenuExpandedContext = React.createContext<MenuExpandedContextValue | null>(null);

/**
 * Menu Expanded Context Provider
 *
 * Provides menu expansion state to all components that need it.
 * State is shared across all consumers and does NOT persist across page reloads.
 *
 * - isExpanded: Forces Phase 3 even on small screens (when user clicks menu item)
 * - isMinimized: Forces Phase 2 even on large screens (when user clicks minimize button)
 */
export const MenuExpandedProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [isExpanded, setIsExpanded] = React.useState<boolean>(false);
	const [isMinimized, setIsMinimized] = React.useState<boolean>(false);

	const setExpanded = React.useCallback(
		(expanded: boolean) => {
			setIsExpanded(expanded);
			// When expanding, clear minimized state
			if (expanded) {
				setIsMinimized(false);
			}
		},
		[],
	);

	const setMinimized = React.useCallback(
		(minimized: boolean) => {
			setIsMinimized(minimized);
			// When minimizing: set expanded=true so sidebar gets sidebar--menu-expanded class.
			// This ensures Account/Internal use the same CSS context as when expanding via item click
			// (menu-minimized overrides still apply for Phase 2 appearance).
			if (minimized) {
				setIsExpanded(true);
			}
			// When un-minimizing: expanded is set separately by NavigationMinimizeToggle
		},
		[],
	);

	const value: MenuExpandedContextValue = React.useMemo(
		() => ({
			isExpanded,
			isMinimized,
			setExpanded,
			setMinimized,
		}),
		[isExpanded, isMinimized, setExpanded, setMinimized],
	);

	return (
		<MenuExpandedContext.Provider value={value}>
			{children}
		</MenuExpandedContext.Provider>
	);
};

/**
 * Hook to use menu expanded context
 *
 * @throws Error if used outside MenuExpandedProvider
 */
export function useMenuExpandedContext(): MenuExpandedContextValue {
	const context = React.useContext(MenuExpandedContext);
	if (!context) {
		throw new Error('useMenuExpandedContext must be used within MenuExpandedProvider');
	}

	return context;
}

export default MenuExpandedContext;
